import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Box, Typography } from "@mui/material";
import { computeNiceAxis } from "../../PagesContent/BatteryIntelligence/SOHAndRUL/utils/niceScale";
import { decimalYearToLabel, computeRULXAxisConfig } from "../../PagesContent/BatteryIntelligence/SOHAndRUL/utils/rulXAxis";
import { useRULBuckets } from "../../PagesContent/BatteryIntelligence/SOHAndRUL/utils/rulBuckets";
import { useBaseEntityInfo } from "../../Hooks/usePagesContent";
import {
  isFiniteValue,
  buildPlotGroups,
  buildScatterSeries,
  buildScatterColors,
  buildScatterMarkers,
  resolveYAxisValues,
  RUL_Y_FALLBACK,
  collectRULAxisXValues,
  isRULPlottable,
  toRULPoint,
  buildLegendLabel,
  getLegendSwatchSx,
} from "./scatterBucketUtils";
import { UNAVAILABLE_BUCKET_COLOR } from "../../PagesContent/BatteryIntelligence/SOHAndRUL/utils/unavailableBucket";

const Y_KEY = { months: "rulMonths", cycles: "rulCycles", km: "rulKm" };

const formatRulMonths = (months) => {
  if (months == null) return "—";
  const years = Math.floor(months / 12);
  const rem   = Math.round(months % 12);
  if (years === 0) return `${rem} mo`;
  if (rem   === 0) return `${years} yr`;
  return `${years} yr ${rem} mo`;
};

const Y_AXIS_TITLES = {
  months: "RUL (in months)",
  cycles: "RUL (in cycles)",
  km:     "RUL (in km)",
};

function smartTooltipPos(clientX, clientY, tooltipW = 215, tooltipH = 165) {
  const M  = 14;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = clientX + M;
  let top  = clientY + M;
  if (left + tooltipW > vw - 8) left = clientX - tooltipW - M;
  if (top  + tooltipH > vh - 8) top  = clientY - tooltipH - M;
  return { left: Math.max(8, left), top: Math.max(8, top) };
}

/**
 * Shared RUL scatter chart — legend + chart + hover tooltip.
 * No data fetching; receives pre-grouped batteries from the parent.
 *
 * Props
 *   batteriesByVisibleCategory  [{category, batteries[]}] — filtered for selection
 *   selectedCategories          Set<string>
 *   onCategorySelect            callback(name) — toggle category selection
 *   activeYFilter               "months" | "cycles" | "km"
 *   chartId                     unique ApexCharts chart ID string
 *   height                      chart height px (default 360)
 *   showLegend                  render the legend (default true)
 *   onDotClick                  optional callback(battery) — enables dot-click navigation
 *   unavailableCount            optional — total "RUL Unavailable" batteries for the legend
 *                               count; defaults to the visible unavailable group's size
 *
 * Range-bucket batteries need a manufacturing date (mfgDecimalYear, x) and a RUL value
 * for the active unit (y) to be plotted. The "RUL Unavailable" group
 * (category.isUnavailable) is a normal series, drawn first (underneath) as solid grey dots at y = 0
 * and their manufacturing date; one without a usable manufacturing date is pinned to
 * the x-axis minimum (never year 0), and does not widen the axis.
 */
const RULScatterChart = ({
  batteriesByVisibleCategory = [],
  selectedCategories,
  onCategorySelect,
  activeYFilter,
  supportedUnits,
  chartId,
  height = 360,
  showLegend = true,
  onDotClick,
  unavailableCount,
}) => {
  const rulBuckets    = useRULBuckets();
  const allCategories = rulBuckets[activeYFilter] ?? [];
  const { baseEntityTypeIDLable } = useBaseEntityInfo();

  const [scatterHover, setScatterHover] = useState(null);
  const visibleGroupsRef = useRef([]);

  // Clear hover when selection or filter changes
  useEffect(() => { setScatterHover(null); }, [selectedCategories, activeYFilter]);

  const yKey = Y_KEY[activeYFilter];

  // Range buckets keep batteries with a manufacturing date and a RUL value; the
  // unavailable bucket (every battery) goes first, so it is drawn underneath. Series, colours, markers, axes and
  // hover/click index lookups all use plottedGroups.
  const { plottedGroups, unavailableTotal } = useMemo(
    () => buildPlotGroups(batteriesByVisibleCategory, { isPlottable: (b) => isRULPlottable(b, yKey) }),
    [batteriesByVisibleCategory, yKey]
  );
  const legendUnavailableCount = Number.isFinite(unavailableCount) ? unavailableCount : unavailableTotal;

  useEffect(() => {
    visibleGroupsRef.current = plottedGroups;
  }, [plottedGroups]);

  // The x range comes from real manufacturing years only (default range when there are
  // none); unavailable batteries without one are then pinned to xAxis.min.
  const xAxis = useMemo(
    () => computeRULXAxisConfig(collectRULAxisXValues(plottedGroups)),
    [plottedGroups]
  );

  const scatterSeries = useMemo(
    () => buildScatterSeries(plottedGroups, (b, category) => toRULPoint(b, yKey, category, xAxis.min)),
    [plottedGroups, yKey, xAxis.min]
  );

  // y comes from real RUL values plus 0 when an unavailable dot is shown, or the default
  // range when there is no real RUL at all. minStep keeps the rounded tick labels distinct.
  const yAxis = useMemo(
    () => computeNiceAxis(resolveYAxisValues(scatterSeries, plottedGroups, RUL_Y_FALLBACK), 6, { minStep: 1, nonNegative: true }),
    [scatterSeries, plottedGroups]
  );

  const yTickAmount   = yAxis.ticks.length - 1;
  const yAxisTitle    = Y_AXIS_TITLES[activeYFilter];
  const scatterColors = useMemo(
    () => buildScatterColors(plottedGroups, UNAVAILABLE_BUCKET_COLOR),
    [plottedGroups]
  );
  const scatterMarkers = useMemo(() => buildScatterMarkers(plottedGroups), [plottedGroups]);

  useEffect(() => {
    try {
      ApexCharts.exec(chartId, "updateOptions", {
        yaxis: {
          min:            yAxis.min,
          max:            yAxis.max,
          tickAmount:     yTickAmount,
          forceNiceScale: false,
          title: {
            text:    yAxisTitle,
            rotate:  -90,
            offsetX: 4,
            style:   { fontSize: "11px", fontWeight: 400, color: "#8F8F8F" },
          },
          labels: {
            formatter: (val) => Math.round(val),
            offsetX:   -8,
            style:     { fontSize: "11px", colors: "#8F8F8F" },
          },
        },
      }, false, false);
    } catch (_) {}
  }, [chartId, yAxis.min, yAxis.max, yTickAmount, yAxisTitle]);

  const handleScatterMouseEnter = useCallback((event, _ctx, config) => {
    const { seriesIndex, dataPointIndex } = config;
    if (seriesIndex < 0 || dataPointIndex < 0) return;
    const group   = visibleGroupsRef.current[seriesIndex];
    const battery = group?.batteries[dataPointIndex];
    if (!battery) return;
    setScatterHover({
      clientX:       event.clientX,
      clientY:       event.clientY,
      battery,
      color:         group.category.color,
      category:      group.category.displayLabel ?? group.category.name,
      isUnavailable: Boolean(group.category.isUnavailable),
    });
  }, []);

  const handleScatterMouseLeave = useCallback(() => setScatterHover(null), []);

  const handleScatterDotClick = useCallback((_event, _ctx, config) => {
    if (!onDotClick) return;
    const { seriesIndex, dataPointIndex } = config;
    if (seriesIndex < 0 || dataPointIndex < 0) return;
    const group   = visibleGroupsRef.current[seriesIndex];
    const battery = group?.batteries[dataPointIndex];
    if (battery) onDotClick(battery);
  }, [onDotClick]);

  const scatterOptions = useMemo(() => ({
    chart: {
      id:   chartId,
      type: "scatter",
      toolbar: {
        show: true,
        tools: {
          download:  false,
          selection: false,
          zoom:      true,
          zoomin:    true,
          zoomout:   true,
          pan:       true,
          reset:     true,
        },
        autoSelected: "zoom",
      },
      animations: { enabled: false },
      zoom: { enabled: true, type: "xy", allowMouseWheelZoom: false },
      events: {
        dataPointMouseEnter: handleScatterMouseEnter,
        dataPointMouseLeave: handleScatterMouseLeave,
        ...(onDotClick ? { dataPointSelection: handleScatterDotClick } : {}),
      },
    },
    colors:     scatterColors,
    legend:     { show: false },
    dataLabels: { enabled: false },
    xaxis: {
      type:       "numeric",
      min:        xAxis.min,
      max:        xAxis.max,
      tickAmount: xAxis.tickAmount,
      labels: {
        formatter:    (val) => decimalYearToLabel(val),
        style:        { fontSize: "11px", colors: "#8F8F8F" },
        rotate:       -35,
        rotateAlways: false,
      },
      title: {
        text:  "Manufacturing Date",
        style: { fontSize: "11px", fontWeight: 400, color: "#8F8F8F" },
        offsetY: 5,
      },
      axisBorder: { show: false },
      axisTicks:  { show: false },
    },
    yaxis: {
      min:            yAxis.min,
      max:            yAxis.max,
      tickAmount:     yTickAmount,
      forceNiceScale: false,
      title: {
        text:    yAxisTitle,
        rotate:  -90,
        offsetX: 4,
        style:   { fontSize: "11px", fontWeight: 400, color: "#8F8F8F" },
      },
      labels: {
        formatter: (val) => Math.round(val),
        offsetX:   -8,
        style:     { fontSize: "11px", colors: "#8F8F8F" },
      },
    },
    grid: {
      borderColor: "#F0F0F0",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
      padding: { left: 14, right: 8 },
    },
    markers: scatterMarkers,
    tooltip: { enabled: false },
    states: {
      hover:  { filter: { type: "darken", value: 0.35 } },
      active: { filter: { type: "none" } },
    },
  }), [
    chartId, xAxis, yAxis, yTickAmount, yAxisTitle,
    scatterColors, scatterMarkers, handleScatterMouseEnter, handleScatterMouseLeave, handleScatterDotClick, onDotClick,
  ]);

  return (
    <Box sx={{ width: "100%" }}>

      {/* ── Legend ──────────────────────────────────────────────────────────── */}
      {showLegend && (
        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "16px", mb: "8px" }}>
          {allCategories.map((cat) => {
            const isSelected   = selectedCategories.has(cat.name);
            const hasSelection = selectedCategories.size > 0;
            const isFaded      = hasSelection && !isSelected;
            return (
              <Box
                key={cat.name}
                data-testid={cat.isUnavailable ? "rul-scatter-legend-unavailable" : `rul-scatter-legend-${cat.name}`}
                onClick={() => onCategorySelect(cat.name)}
                sx={{
                  display: "flex", alignItems: "center", gap: "5px",
                  cursor: "pointer", opacity: isFaded ? 0.35 : 1,
                  transition: "opacity 0.18s", userSelect: "none",
                  "&:hover": { opacity: isFaded ? 0.55 : 0.78 },
                }}
              >
                <Box sx={getLegendSwatchSx(cat, isFaded)} />
                <Typography sx={{ fontSize: "12px", color: "#444B51", whiteSpace: "nowrap", fontWeight: isSelected ? 600 : 400 }}>
                  {buildLegendLabel(cat, legendUnavailableCount)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Chart ───────────────────────────────────────────────────────────── */}
      <Box sx={{ "& .apexcharts-canvas svg": { cursor: onDotClick ? "pointer" : "default" } }}>
        <ReactApexChart
          type="scatter"
          series={scatterSeries}
          options={scatterOptions}
          height={height}
        />
      </Box>

      {/* ── Hover tooltip ───────────────────────────────────────────────────── */}
      {scatterHover && (() => {
        const { battery, color, category, clientX, clientY, isUnavailable } = scatterHover;
        const { left, top } = smartTooltipPos(clientX, clientY, 215, 165);
        const hasMfgDate = isFiniteValue(battery.mfgDecimalYear);
        const mfgDate = hasMfgDate ? decimalYearToLabel(Number(battery.mfgDecimalYear)) : "N/A";
        return (
          <Box sx={{
            position: "fixed", left, top, zIndex: 9999,
            bgcolor: "#fff", border: "1px solid #E6E6E6", borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)", p: "12px 16px",
            minWidth: "205px", pointerEvents: "none",
          }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: "6px",
              mb: "10px", pb: "8px", borderBottom: "1px solid #F0F0F0",
            }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E" }}>
                {category}
              </Typography>
            </Box>
            {(isUnavailable
              ? [
                  // no row for a missing date: the note below says it is not available
                  ...(hasMfgDate ? [["Manufacturing Date", mfgDate]] : []),
                  [baseEntityTypeIDLable, battery.batteryID ?? "—"],
                ]
              : [
                  ["Manufacturing Date", mfgDate],
                  ["RUL (Months & Years)", formatRulMonths(battery.rulMonths)],
                  ["RUL (Cycles)",         battery.rulCycles != null ? battery.rulCycles.toLocaleString() : "—"],
                  ...(!supportedUnits || supportedUnits.includes("km")
                    ? [["RUL (km)", battery.rulKm != null ? `${battery.rulKm.toLocaleString()} km` : "—"]]
                    : []),
                  [baseEntityTypeIDLable, battery.batteryID ?? "—"],
                ]
            ).map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center", gap: "16px", mb: "5px",
                  "&:last-child": { mb: 0 },
                }}
              >
                <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>{label}</Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E" }}>{value}</Typography>
              </Box>
            ))}
            {isUnavailable && (
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mt: "5px" }}>
                RUL not available
              </Typography>
            )}
            {isUnavailable && !hasMfgDate && (
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mt: "2px" }}>
                Manufacturing Date not available
              </Typography>
            )}
          </Box>
        );
      })()}

    </Box>
  );
};

export default RULScatterChart;
